import MarketingLayout from '../../components/marketing/MarketingLayout';
import { Card, Title, Text } from "@tremor/react";
import { useState } from 'react';
import BookingWidget from '../../components/BookingWidget';
import ServicePageNavigation from '../../components/ServicePageNavigation';
import { colors } from '../../lib/colors';

export default function Rrif() {
  const [showBookingWidget, setShowBookingWidget] = useState(false);

  return (
    <MarketingLayout>
      <section className="py-16 px-4 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto relative">
          {/* Service Navigation */}
          <ServicePageNavigation currentSlug="registered-retirement-income-fund-rrif" />
          {/* Header */}
          <div className="text-center mb-12 pt-4"> {/* Added padding top */}
            <Title className="text-4xl font-bold text-gray-800 dark:text-white mb-4">Registered Retirement Income Fund (RRIF)</Title>
            <Text className="text-xl text-gray-600 dark:text-gray-400">
              Converting Your RRSP Savings into Retirement Income
            </Text>
          </div>

          <div className="space-y-8">
            {/* What is a RRIF? */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">What is a RRIF?</Title>
              <Text className="text-gray-600 dark:text-gray-300">
                A Registered Retirement Income Fund (RRIF) is an account designed to provide you with a regular stream of income from the savings you accumulated in your Registered Retirement Savings Plan (RRSP). You must convert your RRSP savings into a RRIF (or an annuity or cash withdrawal) by the end of the year you turn 71.
              </Text>
              <Text className="text-gray-600 dark:text-gray-300 mt-2">
                While you can no longer contribute to a RRIF, the investments held within it continue to grow tax-deferred. However, you are required to withdraw a minimum amount each year, which is considered taxable income.
              </Text>
            </Card>

            {/* How It Works */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">How It Works</Title>
              <ol className="list-decimal list-inside text-gray-600 dark:text-gray-300 space-y-2 mt-4">
                <li>
                  <strong>Conversion from RRSP:</strong> You transfer your RRSP assets directly into a RRIF account, typically without immediate tax consequences. This must be done by December 31st of the year you turn 71.
                </li>
                <li>
                  <strong>Investment Holdings:</strong> Similar investments allowed in an RRSP can generally be held within a RRIF (cash, GICs, bonds, stocks, mutual funds, ETFs).
                </li>
                <li>
                  <strong>Tax-Deferred Growth:</strong> Investments within the RRIF continue to grow sheltered from tax.
                </li>
                <li>
                  <strong>Minimum Annual Withdrawal:</strong> Starting the year after you open the RRIF, you must withdraw a minimum percentage of the RRIF's value (as of the beginning of the year). This percentage increases each year based on your age (or your spouse's age, if elected).
                </li>
                <li>
                  <strong>Taxation of Withdrawals:</strong> All withdrawals from a RRIF (both minimum and any additional amounts) are fully taxable as income in the year received. Withholding tax may apply to amounts withdrawn above the minimum.
                </li>
                 <li>
                  <strong>Flexibility in Withdrawals:</strong> While there's a minimum withdrawal, you can choose to withdraw more than the minimum if needed. You can also typically set the frequency of payments (e.g., monthly, quarterly, annually).
                 </li>
                 <li>
                  <strong>Beneficiary Designation:</strong> You can name a beneficiary (like a spouse or dependent child/grandchild) to potentially allow for a tax-deferred rollover of remaining RRIF assets upon your death.
                 </li>
              </ol>
            </Card>

            {/* Key Benefits */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Key Benefits</Title>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Provides Retirement Income:</strong> Systematically converts RRSP savings into a regular income stream.</li>
                <li><strong>Continued Tax-Deferred Growth:</strong> Remaining funds continue to grow tax-sheltered.</li>
                <li><strong>Investment Control:</strong> You generally maintain control over how the funds within the RRIF are invested.</li>
                <li><strong>Withdrawal Flexibility:</strong> Allows withdrawals exceeding the minimum amount if needed.</li>
                <li><strong>Potential for Tax-Deferred Rollover:</strong> Naming a spouse as beneficiary can defer taxes on remaining assets upon death.</li>
                <li><strong>Payment Frequency Options:</strong> Choose payment schedules that suit your cash flow needs.</li>
              </ul>
            </Card>

            {/* Considerations */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Considerations</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">Important factors to keep in mind:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Mandatory Withdrawals:</strong> You must take out the minimum amount each year, even if you don't need the income, and pay tax on it.</li>
                <li><strong>Withdrawals are Taxable:</strong> All RRIF income is taxed, potentially impacting income-tested benefits like OAS.</li>
                <li><strong>Increasing Minimum Percentage:</strong> The required withdrawal percentage increases with age, potentially depleting funds faster than desired or pushing you into higher tax brackets later in life.</li>
                <li><strong>Investment Risk:</strong> Market downturns can reduce the RRIF value, impacting future income potential, while minimum withdrawals must still be made.</li>
                <li><strong>Longevity Risk:</strong> Unlike annuities, there's a risk of outliving your RRIF savings if withdrawals are too high or investment returns are poor.</li>
                <li><strong>No New Contributions:</strong> You cannot contribute new funds to a RRIF.</li>
              </ul>
            </Card>

            {/* Suitability */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Who Needs a RRIF?</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">A RRIF is the standard vehicle for Canadians who:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li>Have accumulated savings within an RRSP.</li>
                <li>Are approaching or have reached age 71 and need to convert their RRSP.</li>
                <li>Want to draw a regular income from their retirement savings while maintaining investment control and tax deferral on the remaining balance.</li>
                <li>Prefer flexibility in withdrawal amounts (beyond the minimum).</li>
              </ul>
            </Card>

            {/* Call to Action */}
            <div className="text-center mt-12">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-4">Transition Seamlessly into Retirement Income</Title>
              <Text className="text-gray-600 dark:text-gray-400 mb-6 max-w-xl mx-auto">
                Proper RRIF planning is key to managing your retirement income and tax obligations effectively. Let's discuss your RRSP conversion strategy and RRIF withdrawal plan.
              </Text>
              <button
                onClick={() => setShowBookingWidget(true)}
                className="px-8 py-3 rounded-md text-lg font-semibold text-white transition-transform duration-150 ease-in-out hover:scale-105 active:scale-95"
                style={{ background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)` }}
              >
                Plan Your RRIF Strategy
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Widget Modal */}
      {showBookingWidget && (
        <BookingWidget /> // Removed onClose prop
      )}
    </MarketingLayout>
  );
}
